import { useState } from "react";
import { Navigation, Edit3 } from "lucide-react";
import ZipCodeModal from "./ZipCodeModal";
import {
  getIconSize,
  getTextStyles,
  getVariantStyles,
  type ZipCodeVariant,
} from "./ZipCodeDisplay.utils";

interface ZipCodeDisplayProps {
  zipCode: string;
  onZipCodeChange?: (newZipCode: string) => void;
  className?: string;
  showEditButton?: boolean;
  variant?: ZipCodeVariant;
}

export default function ZipCodeDisplay({
  zipCode,
  onZipCodeChange,
  className = "",
  showEditButton = true,
  variant = "default",
}: ZipCodeDisplayProps) {
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);

  const handleZipCodeSuccess = (newZipCode: string) => {
    if (onZipCodeChange) onZipCodeChange(newZipCode);
    setShowZipCodeModal(false);
  };

  const isZipCodeSet = !!zipCode;
  const variantStyles = getVariantStyles(variant);
  const textStyles = getTextStyles(variant);
  const iconSize = getIconSize(variant);
  const isFlat = variant === "flat";

  return (
    <>
      <div className={`${variantStyles} ${className}`}>
        {isFlat ? (
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-dark-300">
              Search Area
            </div>
            <div className="mt-1 flex items-center gap-2">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {isZipCodeSet ? `Near ${zipCode}` : "No ZIP code set"}
              </div>
              {showEditButton && (
                <button
                  type="button"
                  onClick={() => setShowZipCodeModal(true)}
                  className="shrink-0 text-xs font-medium text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  {isZipCodeSet ? "Change" : "Set"}
                </button>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">
              {isZipCodeSet ? "Nearby stores are sorted from this ZIP code." : "Set a ZIP code to sort nearby stores."}
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-primary-600/10 dark:bg-primary-400/10 sm:h-8 sm:w-8">
                <Navigation className={`${iconSize} text-primary-600 dark:text-primary-400`} />
              </div>
              <div>
                <span className={textStyles}>
                  {variant === "inline"
                    ? isZipCodeSet ? `Near ${zipCode}` : "Set location"
                    : isZipCodeSet ? `Searching near ${zipCode}` : "No zip code set"}
                </span>
                {variant === "default" && isZipCodeSet && (
                  <p className="mt-0.5 hidden text-[11px] text-gray-400 dark:text-dark-500 sm:block">
                    Stores sorted by distance from your area
                  </p>
                )}
              </div>
            </div>
            {showEditButton && (
              <button
                type="button"
                onClick={() => setShowZipCodeModal(true)}
                className="ml-2 flex flex-shrink-0 items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-primary-600 transition-colors hover:bg-gray-200 hover:text-primary-700 dark:bg-dark-700 dark:text-primary-400 dark:hover:bg-dark-600 dark:hover:text-primary-300"
              >
                <Edit3 className="h-3 w-3 flex-shrink-0" />
                <span>{isZipCodeSet ? "Change" : "Set"}</span>
              </button>
            )}
          </div>
        )}
      </div>

      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => setShowZipCodeModal(false)}
        onSuccess={handleZipCodeSuccess}
        currentZipCode={zipCode}
        title={isZipCodeSet ? "Update Zip Code" : "Set Zip Code"}
        description="Enter your zip code to search for nearby stores."
      />
    </>
  );
}
